import { CalendarToday, Close, Key } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { addDays, parseISO } from 'date-fns';
import esLocale from 'date-fns/locale/es';
import { useFormik } from 'formik';
import { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
import {
  AlphanumericIcon,
  DateEditIcon,
  DateEndIcon,
  DateStartIcon,
} from '../../../../assets/icons';
import { FieldDisable, ModalBar } from '../../../../components';
import { useLoading, useNotify } from '../../../../hooks';
import {
  NivelService,
  PeriodService,
  ProgramService,
} from '../../../../services';
import { validationMsj } from '../../../../utils/consts';
import { DateHelper } from '../../../../utils/helpers';
import { NivelDTO, PeriodoDTO, ProgramaDTO } from '../../../../utils/models';
import { usePeriods } from '../hooks/usePeriods';

interface Props {
  onReloadData: () => void;
}

const localeMap = {
  es: esLocale,
};

const validationSchema = Yup.object({
  id: Yup.string()
    .required(validationMsj.is.required)
    .max(6, validationMsj.is.maxLength(6))
    .matches(/^\d+$/, validationMsj.is.onlyNumber)
    .trim(validationMsj.is.trim()),
  nombre: Yup.string()
    .required(validationMsj.is.required)
    .trim(validationMsj.is.trim()),

  fechaPeriodoInicio: Yup.date().typeError('La fecha es inválida').required(validationMsj.is.required),

  fechaPeriodoFin: Yup.date().typeError('La fecha es inválida')
    .required(validationMsj.is.required)
    .min(
      Yup.ref('fechaPeriodoInicio'),
      'La fecha de fin debe ser mayor o igual a la fecha de inicio',
    )
    /* .max(
      Yup.ref('fechaVacacionesInicio'),
      'La fecha de fin debe ser menor a la fecha de inicio de vacaciones',
    ) */,

  fechaVacacionesInicio: Yup.date().typeError('La fecha es inválida')
    .required(validationMsj.is.required)
    .min(
      Yup.ref('fechaPeriodoFin'),
      'La fecha de inicio de vacaciones debe ser mayor a la fecha fin del periodo ',
    )
    /* .max(
      Yup.ref('fechaVacacionesFin'),
      'La fecha de inicio de vacaciones debe ser menor a la fecha fin del periodo vacacional ',
    ) */,

  fechaVacacionesFin: Yup.date().typeError('La fecha es inválida')
    .required(validationMsj.is.required)
    .min(
      Yup.ref('fechaVacacionesInicio'),
      'La fecha de fin de vacaciones debe ser mayor o igual a la fecha de inicio de vacaciones',
    ),

  polizaNumero: Yup.string()
    .required(validationMsj.is.required)
    .trim(validationMsj.is.trim()),

  polizaFechaVigencia: Yup.date().typeError('La fecha es inválida')
    .required(validationMsj.is.required)
    .min(
      Yup.ref('fechaVacacionesFin'),
      'La póliza no puede vencer antes de la fecha de fin de vacaciones',
    ),
  esSemestreEmpresarial: Yup.boolean(),
});

export const PeriodRecord: FC<Props> = ({ onReloadData }) => {
  const { modalRecord, changeModal } = usePeriods();
  const nivelService = new NivelService();
  const programService = new ProgramService();
  const { setLoading } = useLoading();
  const periodService = new PeriodService();
  const { notify, setNotify } = useNotify();
  const [levelStudyList, setlevelStudyList] = useState<NivelDTO[]>([]);
  const [programList, setProgramList] = useState<ProgramaDTO[]>([]);
  const formik = useFormik({
    initialValues: {
      id: '',
      nombre: '',
      fechaPeriodoInicio: null,
      fechaPeriodoFin: null,
      fechaVacacionesInicio: null,
      fechaVacacionesFin: null,
      polizaNumero: '',
      polizaFechaVigencia: new Date(),
      activo: true,
      nivelId: '',
      sufijoPrograma: '',
      esSemestreEmpresarial: false,
    },
    validationSchema,

    onSubmit: () => handleSubmit(),
  });

  const resetForm = () => {
    const { id, nivelId, sufijoPrograma, esSemestreEmpresarial } =
      formik.values;
    formik.resetForm();
    formik.setFieldValue('id', id);
    formik.setFieldValue('nivelId', nivelId);
    formik.setFieldValue('sufijoPrograma', sufijoPrograma);
    formik.setFieldValue('esSemestreEmpresarial', esSemestreEmpresarial);
  };

  const isValid = () => {
    const diffDate = DateHelper.compareDates(
      formik.values.fechaPeriodoInicio,
      formik.values.fechaPeriodoFin,
    );
    if (diffDate <= 0) {
      setNotify({
        ...notify,
        open: true,
        message: 'La fecha de inicio de debe ser menor que la fecha final',
        type: 'info',
      });
      return false;
    }
    const diffDateVacations = DateHelper.compareDates(
      formik.values.fechaVacacionesInicio,
      formik.values.fechaVacacionesFin,
    );
    if (diffDateVacations <= 0) {
      setNotify({
        ...notify,
        open: true,
        message:
          'La fecha de inicio de vacaciones debe ser menor que la fecha final de vacaciones',
        type: 'info',
      });
      return false;
    }

    const diffPolicy = DateHelper.compareDates(
      formik.values.fechaVacacionesFin,
      formik.values.polizaFechaVigencia,
    );

    if (diffPolicy <= 0) {
      setNotify({
        ...notify,
        open: true,
        message: 'La fecha de la póliza debe ser mayor que la fecha final',
        type: 'info',
      });
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!formik.isValid) {
      return;
    }

    if (!isValid()) {
      return;
    }

    const body = new PeriodoDTO().deserialize(formik.values);

    switch (modalRecord.type) {
      case 'add':
        body.activo = true;
        createPeriod(body);
        return;
      case 'edit':
        updatePeriod(body);
        return;
    }
  };

  const handleverifyPeriodId = () => {
    resetForm();
    const body = new PeriodoDTO().deserialize(formik.values);
    if (body.id) {
      getBannerPeriodValid(body.id);
    }
  };

  const handleChangeLevel = (value: string) => {
    const { id } = formik.values;
    formik.resetForm();
    formik.setFieldValue('id', id);
    formik.setFieldValue('nivelId', value);
    setProgramList([]);
    formik.setFieldValue('sufijoPrograma', '');
    getProgramSuffixByLevel(value);
  };

  const handleChangeProgramSuffix = (value: string) => {
    const { id, nivelId } = formik.values;
    formik.resetForm();
    formik.setFieldValue('id', id);
    formik.setFieldValue('nivelId', nivelId);
    formik.setFieldValue('sufijoPrograma', value);
  };

  const handleChangeKey = (value: string) => {
    const { nivelId, sufijoPrograma } = formik.values;
    formik.resetForm();
    formik.setFieldValue('id', value);
    formik.setFieldValue('nivelId', nivelId);
    formik.setFieldValue('sufijoPrograma', sufijoPrograma);
  };

  const handleChangeSemester = (value: boolean) => {
    const { id, nivelId, sufijoPrograma } = formik.values;
    formik.resetForm();
    formik.setFieldValue('id', id);
    formik.setFieldValue('nivelId', nivelId);
    formik.setFieldValue('sufijoPrograma', sufijoPrograma);
    formik.setFieldValue('esSemestreEmpresarial', value);
  };

  const getAllLevels = async () => {
    setLoading(true);
    await nivelService
      .getAllLevels()
      .then((response) => {
        setLoading(false);
        const res = response.data;
        if (res.success) {
          const data: NivelDTO[] = res.data.map((user) =>
            new NivelDTO().deserialize(user),
          );
          setlevelStudyList(data);
        } else {
          setNotify({
            ...notify,
            open: true,
            message: res.message,
            type: 'error',
          });
        }
      })
      .catch(() => {
        setLoading(false);
      });
    setLoading(false);
  };

  const getProgramSuffixByLevel = async (levelId: string) => {
    setLoading(true);
    await programService
      .getProgramSufixByLevel(levelId)
      .then((response) => {
        setLoading(false);
        const res = response.data;
        if (res.success) {
          if (res.data.length === 0) {
            setNotify({
              ...notify,
              open: true,
              message: 'No se encontraron sufijos de programas',
              type: 'info',
            });
            return;
          }
          const data: ProgramaDTO[] = res.data.map((user) =>
            new ProgramaDTO().deserialize(user),
          );
          setProgramList(data);
        } else {
          setNotify({
            ...notify,
            open: true,
            message: res.message,
            type: 'error',
          });
        }
      })
      .catch(() => {
        setLoading(false);
        setNotify({
          ...notify,
          open: true,
          message: 'Ocurrio un error al obtener los sufijos de programas',
          type: 'error',
        });
      });
    setLoading(false);
  };

  const createPeriod = async (period: PeriodoDTO) => {
    setLoading(true);
    await periodService
      .createPeriod(period)
      .then((response) => {
        setLoading(false);
        const res = response.data;
        if (res.success) {
          setNotify({
            ...notify,
            open: true,
            message: 'Se creó el periodo correctamente.',
            type: 'success',
          });
          changeModal({ ...modalRecord, show: false, id: null });
          onReloadData();
        } else {
          setNotify({
            ...notify,
            open: true,
            message: res.message,
            type: 'error',
          });
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const updatePeriod = async (period: PeriodoDTO) => {
    setLoading(true);
    await periodService
      .updatePeriod(period)
      .then((response) => {
        setLoading(false);
        const res = response.data;
        if (res.success) {
          setNotify({
            ...notify,
            open: true,
            message: 'Se actualizó el periodo correctamente.',
            type: 'success',
          });
          changeModal({ ...modalRecord, show: false, id: null });
          onReloadData();
        } else {
          setNotify({
            ...notify,
            open: true,
            message: res.message,
            type: 'error',
          });
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getPeriodById = async (period: PeriodoDTO) => {
    setLoading(true);

    if (!period) {
      setLoading(false);
      setNotify({
        ...notify,
        open: true,
        message: 'Ocurrio un error al obtener el periodo',
        type: 'error',
      });
      return;
    }
    await periodService
      .getPeriodById(
        period.id,
        period.nivelId,
        period.sufijoPrograma,
        period.esSemestreEmpresarial,
      )
      .then((response) => {
        setLoading(false);
        const res = response.data;

        formik.setValues({
          ...formik.values,
          id: res.data.id,
          nombre: res.data.nombre,
          fechaPeriodoInicio: parseISO(res.data.fechaPeriodoInicio),
          fechaPeriodoFin: parseISO(res.data.fechaPeriodoFin),
          fechaVacacionesInicio: parseISO(res.data.fechaVacacionesInicio),
          fechaVacacionesFin: parseISO(res.data.fechaVacacionesFin),
          polizaNumero: res.data.polizaNumero,
          polizaFechaVigencia: parseISO(res.data.polizaFechaVigencia),
          activo: res.data.activo,
          nivelId: res.data.nivelId,
          sufijoPrograma: res.data.sufijoPrograma,
          esSemestreEmpresarial: res.data.esSemestreEmpresarial,
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getBannerPeriodValid = async (periodId: string) => {
    if (!/^\d+$/.test(periodId)) {
      setNotify({
        ...notify,
        open: true,
        message: 'Solo se permite agregar números a la clave del periodo',
        type: 'info',
      });
      return;
    }
    const values = formik.values;
    const programSuffix = values.sufijoPrograma;
    const level = values.nivelId;
    const isSemesterBusiness = values.esSemestreEmpresarial;

    if (!level) {
      setNotify({
        ...notify,
        open: true,
        message: 'Favor de seleccionar el nivel de estudios',
        type: 'info',
      });
      return;
    }
    if (!programSuffix) {
      setNotify({
        ...notify,
        open: true,
        message: 'Favor de seleccionar el sufijo del programa',
        type: 'info',
      });
      return;
    }

    setLoading(true);
    formik.validateForm();
    await periodService
      .getBannerPeriodValid(periodId, level, programSuffix, isSemesterBusiness)
      .then((response) => {
        setLoading(false);
        const res = response.data;

        if (res.success) {
          formik.setValues({
            ...formik.values,
            id: res.data.stvTermCode,
            nombre: res.data.descPeriodo,
            fechaPeriodoInicio: parseISO(res.data.fechaInicial),
            fechaPeriodoFin: parseISO(res.data.fechaFinal),
            fechaVacacionesInicio: parseISO(res.data.fechaInicialVacacional),
            fechaVacacionesFin: parseISO(res.data.fechaFinalVacacional),
          });
          if (res.data.fechaFinalVacacional < res.data.fechaInicial) {
            setNotify({
              ...notify,
              open: true,
              message:
                'La fecha de fin del periodo de vacaciones aún no está registrada en el sistema Banner.',
              type: 'info',
            });
            return false;
          }
        } else {
          setNotify({
            ...notify,
            open: true,
            message: res.message,
            type: 'info',
          });
          resetForm();
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const errorDatePicker = (value: boolean) => {
    return value ? 'Ingrese una fecha válida.' : '';
  };

  const handleClose = () => {
    formik.resetForm();
    changeModal({ ...modalRecord, show: false, id: null });
  };

  useEffect(() => {
    getAllLevels();
    if (modalRecord.id) {
      getPeriodById(modalRecord.periodEdit);
    }
  }, [modalRecord]);

  return (
    <>
      <Dialog open={modalRecord.show} fullWidth maxWidth={'md'}>
        <DialogTitle>
          {modalRecord.type === 'add'
            ? 'Crear un periodo nuevo'
            : 'Editar los datos del periodo'}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
            }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {modalRecord.type === 'add' && (
            <>
              <ModalBar
                startIcon={<CalendarToday sx={{ color: 'grayCustom.main' }} />}
                title="Define el intervalo de los periodos y los datos de la póliza"
              />
            </>
          )}
          {modalRecord.type === 'edit' && (
            <>
              <ModalBar
                startIcon={<DateEditIcon />}
                title="Edita el intervalo de los periodos y los datos de la póliza"
              />
            </>
          )}
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={localeMap.es}>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  color="primary.darker"
                  sx={{ fontWeight: 600, pb: 2 }}>
                  DATOS DEL PERIODO
                </Typography>
              </Grid>
              {modalRecord.type === 'add' && (
                <Grid item xs={12} md={6} sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    size={'small'}
                    sx={{ my: 2 }}
                    required
                    error={!!formik.errors.nivelId}>
                    <InputLabel sx={{ pt: 1 }}>Nivel</InputLabel>
                    <Select
                      name="nivelId"
                      autoWidth
                      label="Nivel"
                      placeholder="Selecciona el nivel"
                      value={formik.values.nivelId}
                      onChange={(event) =>
                        handleChangeLevel(event.target.value)
                      }
                      aria-describedby="levelHelperText">
                      {levelStudyList
                        .sort((a, b) => a.nombre.localeCompare(b.nombre))
                        .map((levelStudy) => (
                          <MenuItem
                            key={levelStudy.nivelId}
                            value={levelStudy.nivelId}>
                            {levelStudy.nombre}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText id="levelHelperText">
                      {formik.errors.nivelId as string}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              )}
              {modalRecord.type === 'add' && (
                <>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size={'small'}
                      sx={{ my: 2 }}
                      required
                      error={!!formik.errors.sufijoPrograma}>
                      <InputLabel sx={{ pt: 1 }}>
                        Sufijo de Programas
                      </InputLabel>
                      <Select
                        name="sufijoPrograma"
                        autoWidth
                        label="Nivel"
                        placeholder="Selecciona el nivel"
                        value={formik.values.sufijoPrograma}
                        onChange={(event) =>
                          handleChangeProgramSuffix(event.target.value)
                        }
                        aria-describedby="levelHelperText">
                        {programList
                          .sort((a, b) => a.sufijo.localeCompare(b.sufijo))
                          .map((program) => (
                            <MenuItem
                              key={program.sufijo}
                              value={program.sufijo}>
                              {program.sufijo}
                            </MenuItem>
                          ))}
                      </Select>
                      <FormHelperText id="levelHelperText">
                        {formik.errors.sufijoPrograma as string}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                    <TextField
                      value={formik.values.id}
                      onChange={(event) => handleChangeKey(event.target.value)}
                      name="id"
                      label="Clave"
                      variant="outlined"
                      type="text"
                      fullWidth
                      required
                      inputProps={{
                        maxLength: 6,
                        readOnly: modalRecord.type === 'add' ? false : true,
                      }}
                      error={Boolean(formik.errors.id)}
                      helperText={formik.errors.id}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="esSemestreEmpresarial"
                            checked={formik.values.esSemestreEmpresarial}
                            onChange={(event) =>
                              handleChangeSemester(event.target.checked)
                            }
                          />
                        }
                        label="Periodo para Semestre Empresarial"
                      />
                    </FormGroup>
                  </Grid>
                </>
              )}
              {modalRecord.type === 'edit' && (
                <>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                    <FieldDisable
                      icon={<Key sx={{ color: 'grayCustom.main' }} />}
                      title="Nivel"
                      subtitle={formik.values.nivelId}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                    <FieldDisable
                      icon={<Key sx={{ color: 'grayCustom.main' }} />}
                      title="Sufijo de Programas"
                      subtitle={formik.values.sufijoPrograma}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                    <FieldDisable
                      icon={<Key sx={{ color: 'grayCustom.main' }} />}
                      title="Clave"
                      subtitle={formik.values.id}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                    <FieldDisable
                      icon={<Key sx={{ color: 'grayCustom.main' }} />}
                      title="Semestre empresarial"
                      subtitle={
                        formik.values.esSemestreEmpresarial ? 'Si' : 'No'
                      }
                    />
                  </Grid>
                </>
              )}
              {modalRecord.type === 'add' && (
                <Grid item xs={12} md={2} sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleverifyPeriodId}
                    disabled={
                      formik.values.id === '' ||
                      formik.values.id.length !== 6 ||
                      !formik.values.nivelId ||
                      !formik.values.sufijoPrograma ||
                      !/^\d+$/.test(formik.values.id)
                    }
                    sx={{
                      ml: { xs: 0, md: 2 },
                      my: 1,
                      py: 1,
                      mt: 1.5,
                      width: { xs: '100%', md: 'auto' },
                      justifyContent: { xs: 'flex-start', md: 'center' },
                    }}>
                    Validar
                  </Button>
                </Grid>
              )}
              <Grid
                item
                xs={12}
                md={modalRecord.type === 'edit' ? 12 : 6}
                sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                <TextField
                  value={formik.values.nombre}
                  onChange={formik.handleChange}
                  name="nombre"
                  label="Descripción"
                  variant="outlined"
                  type="text"
                  fullWidth
                  disabled={true}
                  required
                  inputProps={{ maxLength: 150 }}
                  error={Boolean(formik.errors.nombre)}
                  helperText={formik.errors.nombre}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                <DatePicker
                  label="Inicio del periodo"
                  inputFormat="dd/MM/yyyy"
                  value={formik.values.fechaPeriodoInicio}
                  onChange={(event) => {
                    formik.setFieldValue('fechaPeriodoInicio', event);
                  }}
                  components={{
                    OpenPickerIcon: DateStartIcon,
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="dd/MM/yyyy"
                      required
                      error={Boolean(formik.errors.fechaPeriodoInicio)}
                      helperText={
                        formik.errors.fechaPeriodoInicio
                          ? typeof formik.errors.fechaPeriodoInicio === 'string'
                            ? formik.errors.fechaPeriodoInicio
                            : errorDatePicker(true)
                          : ''
                      }
                    />
                  )}
                  maxDate={formik.values.fechaPeriodoFin}
                  disabled={!formik.values.nombre}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                <DatePicker
                  label="Fin del periodo"
                  inputFormat="dd/MM/yyyy"
                  value={formik.values.fechaPeriodoFin}
                  onChange={(event) => {
                    formik.setFieldValue('fechaPeriodoFin', event);
                  }}
                  components={{
                    OpenPickerIcon: DateEndIcon,
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Fin del periodo"
                      placeholder="dd/MM/yyyy"
                      required
                      error={Boolean(formik.errors.fechaPeriodoFin)}
                      helperText={
                        formik.errors.fechaPeriodoFin
                          ? typeof formik.errors.fechaPeriodoFin === 'string'
                            ? formik.errors.fechaPeriodoFin
                            : errorDatePicker(true)
                          : ''
                      }
                    />
                  )}
                  minDate={
                    formik.values.fechaPeriodoInicio
                      ? addDays(formik.values.fechaPeriodoInicio, 1)
                      : null
                  }
                  disabled={!formik.values.nombre}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                <DatePicker
                  label="Inicio del periodo vacacional"
                  inputFormat="dd/MM/yyyy"
                  value={formik.values.fechaVacacionesInicio}
                  onChange={(event) => {
                    formik.setFieldValue('fechaVacacionesInicio', event);
                  }}
                  components={{
                    OpenPickerIcon: DateStartIcon,
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="dd/MM/yyyy"
                      required
                      error={Boolean(formik.errors.fechaVacacionesInicio)}
                      helperText={
                        formik.errors.fechaVacacionesInicio
                          ? typeof formik.errors.fechaVacacionesInicio ===
                            'string'
                            ? formik.errors.fechaVacacionesInicio
                            : errorDatePicker(true)
                          : ''
                      }
                    />
                  )}
                  minDate={
                    formik.values.fechaPeriodoFin
                      ? addDays(formik.values.fechaPeriodoFin, 1)
                      : null
                  }
                  disabled={!formik.values.nombre}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                <DatePicker
                  label="Fin del periodo vacacional"
                  inputFormat="dd/MM/yyyy"
                  value={formik.values.fechaVacacionesFin}
                  onChange={(event) => {
                    formik.setFieldValue('fechaVacacionesFin', event);
                  }}
                  components={{
                    OpenPickerIcon: DateEndIcon,
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Fin del periodo vacacional"
                      placeholder="dd/MM/yyyy"
                      required
                      error={Boolean(formik.errors.fechaVacacionesFin)}
                      helperText={
                        formik.errors.fechaVacacionesFin
                          ? typeof formik.errors.fechaVacacionesFin === 'string'
                            ? formik.errors.fechaVacacionesFin
                            : errorDatePicker(true)
                          : ''
                      }
                    />
                  )}
                  minDate={
                    formik.values.fechaVacacionesInicio
                      ? addDays(formik.values.fechaVacacionesInicio, 1)
                      : null
                  }
                  disabled={!formik.values.nombre}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  color="primary.darker"
                  sx={{ fontWeight: 600, pb: 2 }}>
                  DATOS DE LA PÓLIZA
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                <TextField
                  value={formik.values.polizaNumero}
                  onChange={formik.handleChange}
                  name="polizaNumero"
                  label="Póliza de seguros vigente"
                  variant="outlined"
                  type="text"
                  fullWidth
                  required
                  inputProps={{ maxLength: 50 }}
                  error={Boolean(formik.errors.polizaNumero)}
                  helperText={formik.errors.polizaNumero}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <AlphanumericIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ py: 2, pr: { xs: 0, md: 2 } }}>
                <DatePicker
                  onChange={(event) => {
                    formik.setFieldValue('polizaFechaVigencia', event);
                  }}
                  value={formik.values.polizaFechaVigencia}
                  inputFormat="dd/MM/yyyy"
                  label="Vencimiento de la póliza de seguros"
                  components={{
                    OpenPickerIcon: DateEndIcon,
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="dd/MM/yyyy"
                      required
                      error={Boolean(formik.errors.polizaFechaVigencia)}
                      helperText={
                        formik.errors.polizaFechaVigencia
                          ? typeof formik.errors.polizaFechaVigencia ===
                            'string'
                            ? formik.errors.polizaFechaVigencia
                            : errorDatePicker(true)
                          : ''
                      }
                    />
                  )}
                  minDate={
                    formik.values.fechaVacacionesFin
                      ? addDays(formik.values.fechaVacacionesFin, 1)
                      : null
                  }
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ m: 2, display: { xs: 'block', md: 'flex' } }}>
          <Button
            type="submit"
            variant="outlined"
            onClick={handleClose}
            sx={{
              textTransform: 'uppercase',
              width: { xs: '100%', md: 'auto' },
              py: 1,
              m: 1,
            }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!(formik.isValid && formik.dirty)}
            sx={{
              textTransform: 'uppercase',
              width: { xs: '100%', md: 'auto' },
              py: 1,
              m: 1,
            }}>
            {modalRecord.type === 'add' ? 'Crear periodo' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
